import { useEffect } from 'react';
import { Button } from '@Components/Button';
import { HorizontalBarChart } from '@Components/Charts/HorizontalBarChart/HorizontalBarChart';
import { Design, Size } from '@Components/designConstants';
import { toPrettyNumber } from '@src/utils/performance-utils';
import { TOP_ATTRIBUTES_LIMIT } from '../alexa-visibility-constants';
import {
  AttributeCount,
  AttributeShare,
  formatAttributeShareLabel,
  getAttributeShares
} from '../alexa-visibility-utils';

// Tighter than the on-screen charts so two brands fit on one printed page.
const PRINT_ROW_HEIGHT = 16;
const PRINT_AXIS_HEIGHT = 32;
// Long enough for the browser's print dialog to be sure the charts have drawn.
const PRINT_DELAY_MS = 600;

export interface PrintBrand {
  brand: string;
  isTargetBrand: boolean;
  color: string;
  attributes: AttributeCount[];
}

interface AttributesPrintViewProps {
  targetBrand: string;
  brands: PrintBrand[];
  onClose: () => void;
}

// Shown in place of the Attributes tab while the browser's print dialog is open (the same
// approach as the Google Ads Audit PDF export): the app's print styles already hide the nav and
// the page header, and `.no-print` hides this view's own buttons. Only each brand's top
// attributes are listed. The view has a fixed width, narrower than a Letter or A4 page, because
// the charts are drawn at the on-screen width and the print dialog doesn't redraw them.
export const AttributesPrintView = ({ targetBrand, brands, onClose }: AttributesPrintViewProps) => {
  useEffect(() => {
    const timeout = setTimeout(() => window.print(), PRINT_DELAY_MS);
    // Printing or cancelling both end the session; go back to the normal tab.
    window.addEventListener('afterprint', onClose);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('afterprint', onClose);
    };
  }, [onClose]);

  return (
    <div className='aars-print-view'>
      <div className='aars-print-actions no-print'>
        <Button size={Size.SM} onClick={() => window.print()}>
          Save as PDF
        </Button>
        <Button design={Design.NEUTRAL} size={Size.SM} onClick={onClose}>
          Close
        </Button>
      </div>
      <div className='aars-tab-header'>
        <div className='aars-tab-title'>Product attributes overview</div>
        <div className='aars-tab-description'>
          A brand-by-brand breakdown of the product attributes Alexa associates with each product,
          revealing what features drive citations, where {targetBrand} leads, and where competitors
          hold the advantage.
        </div>
      </div>
      {brands.map(({ brand, isTargetBrand, color, attributes }) => {
        const shown = getAttributeShares(attributes).slice(0, TOP_ATTRIBUTES_LIMIT);
        const totalCitations = attributes.reduce((sum, { count }) => sum + count, 0);
        return (
          <div key={brand} className='aars-chart-card aars-print-brand'>
            <div className='aars-chart-card-header'>
              <div className='aars-chart-card-title'>
                <span className='aars-brand-chip' style={{ borderColor: color }}>
                  {brand}
                </span>
                {isTargetBrand && <span className='aars-target-brand-tag'>Target</span>}
                <span className='aars-chart-card-subtitle'>
                  {attributes.length} unique attributes · {toPrettyNumber(totalCitations)} total
                  citations · showing top {shown.length}
                </span>
              </div>
            </div>
            <HorizontalBarChart<AttributeShare>
              data={shown}
              barKey='count'
              yAxisKey='attribute'
              yAxisLabel='Citations'
              barFillColor={color}
              getBarLabel={formatAttributeShareLabel}
              pageLimit={TOP_ATTRIBUTES_LIMIT}
              fixedHeight
              height={shown.length * PRINT_ROW_HEIGHT + PRINT_AXIS_HEIGHT}
            />
          </div>
        );
      })}
    </div>
  );
};
